/**
 * Spielgefühl card renderer. Creates CSS/SVG playing cards with geometric
 * suit pips, distinctive suit colors, and abstract face card motifs.
 * Cards are the soul of the game — they pop against the dark background
 * and feel crafted, modern, and digital-native.
 *
 * Unified "Artisan" Collection (Modern Heritage).
 */
import {
  SUIT_COLORS as SUIT_HEX_COLORS,
  TRUMP_COLOR,
  ANIMATION,
} from "../constants.js";
import { t } from "../i18n.js";
import { effectiveCardStyle } from "../card-style.js";
import { rankStyle } from "../rank-style.js";
import { invertCardColors } from "./card-invert.js";
import { SVG_NS, s } from "./card-svg.js";
import {
  adjustGradient,
  addCardTrumpGradient,
  revealTrumpGradient,
  CARD_TRUMP_GRADIENTS,
} from "./card-gradients.js";
import { addFaceMotif, addAceMotif } from "./card-motifs.js";
import { createBackSvg, createCardBack } from "./card-backs.js";
import { addArtisanPip, addPipArrangement } from "./card-pips.js";
import { getSharedDefs } from "./shared-defs.js";
import { cloneFaceTemplate } from "./card-template-cache.js";
import { CARD_GEOMETRY } from "./card-geometry.js";

export { createCardBack };

export const SUIT_COLORS = { ...SUIT_HEX_COLORS, grand: TRUMP_COLOR };

const NUMERIC_RANKS = { seven: "7", eight: "8", nine: "9", ten: "10" };

const POINT_VALUES = {
  seven: 0,
  eight: 0,
  nine: 0,
  jack: 2,
  queen: 3,
  king: 4,
  ten: 10,
  ace: 11,
};

/** Adds concentric tally arcs in the bottom-left corner — one arc per card point. */
function addPointValueTally(svg, rank, color) {
  const points = POINT_VALUES[rank] || 0;
  const { tallyAspect, tallyRunBase, tallyRunIncrement, edgeThickness } =
    CARD_GEOMETRY;
  // Anchor is mid-stripe (half the L-border's thickness in from each edge) so each arc emerges
  // from within the colored fill rather than landing against its inner edge — derived from
  // edgeThickness rather than hardcoded, so retuning the border keeps the anchor centered on it.
  const anchorX = edgeThickness / 2;
  const anchorY = 350 - edgeThickness / 2;
  for (let k = 1; k <= points; k++) {
    // Elliptical arc, rise = tallyAspect × run — golden (φ) by default since 2026-07-19 (DESIGN
    // #15); the tally is its own etched motif rather than an echo of the 5:7 card silhouette.
    const rx = tallyRunBase + tallyRunIncrement * k;
    const ry = rx * tallyAspect;
    svg.appendChild(
      s("path", {
        d: `M ${anchorX},${anchorY - ry} A ${rx},${ry} 0 0 0 ${anchorX + rx},${anchorY}`,
        stroke: color,
        "stroke-width": 2,
        fill: "none",
      }),
    );
  }
}

// Jack/Queen have a second key variant (card-preferences design § B) picking the glyph set
// (german U/O vs french B/D) — language-blind, since both variants resolve in every bundle
// (rankJackFrench/rankQueenFrench = J/Q in English, so the EN corner label is unaffected either
// way). King/Ace have no variant — Altenburger and French naming already agree on them.
const FRENCH_RANK_KEY = { jack: "rankJackFrench", queen: "rankQueenFrench" };
const GERMAN_RANK_KEY = { jack: "rankJack", queen: "rankQueen" };

/** The rendered rank label — exported so hints.js's inline card refs share this one mechanism
 *  instead of a duplicate mini-map (rankStyle only needs resolving in one place). */
export function rankDisplay(rank) {
  if (NUMERIC_RANKS[rank]) return NUMERIC_RANKS[rank];
  const variantKey =
    rankStyle() === "french" ? FRENCH_RANK_KEY : GERMAN_RANK_KEY;
  const key =
    variantKey[rank] ?? "rank" + rank.charAt(0).toUpperCase() + rank.slice(1);
  return t(key);
}

// --- Corner labels (rank text + small pip) ---

/**
 * Composes a corner ensemble's transform: `CARD_GEOMETRY.cornerNudgeX`/`cornerNudgeY` slide the
 * ensemble toward its own corner (positive = less edge padding), then
 * `CARD_GEOMETRY.cornerScale` scales it about `CORNER_SCALE_ANCHOR` — the ensemble's own visual
 * corner, not the card's (0, 0). Anchoring at
 * (0, 0) would multiply the content's built-in offset from the corner (text top ≈ y 16, left
 * edge ≈ x 7 for "10"), sliding the whole ensemble toward the card center as it grows; anchoring
 * at the visual corner keeps the corner gap constant so the ensemble fattens in place. Both
 * corners share one anchor since bottom-right is authored in the same pre-rotation local frame
 * as top-left and only reaches its actual corner via the existing `rotate(180, 125, 175)`.
 * Appending the scale after that rotate in the transform list applies it first (SVG composes
 * right-to-left), so it scales in the local frame before the rotate carries it to the
 * bottom-right corner — no extra wrapper group needed, unlike `withMotifScale`, since there's
 * already exactly one group per corner to extend. At identity values (scale 1, nudges 0,
 * offsets {}) every suffix is empty and `svg`'s corner `<g>` gets the exact same attrs as
 * before these knobs existed (`{}` for top-left, `{ transform: "rotate(180, 125, 175)" }` for
 * bottom) — the shipped defaults are non-identity since the 2026-07-16 verdict, so production
 * corners do carry the full chain.
 *
 * `gx`/`gy` (from `CARD_GEOMETRY.cornerInkOffsets`, docs/DESIGN.md #15) are the ink-normalized
 * layout's per-card group correction, in the same final-units frame as the nudge translate — the
 * two compose as plain addition, so their order relative to each other doesn't matter. Placed
 * after `rotate(180, ...)` for the bottom copy (matching `nudge`/`scaleSuffix`) so it acts in the
 * ensemble's own local corner frame, not the card's root frame — one solved value serves both
 * corners identically.
 */
const CORNER_SCALE_ANCHOR = 12; // play-test: the ensemble's visual corner (x and y) — the point whose gap to the card corner stays fixed as the ensemble scales
function cornerLabelTransform(isBottom, gx, gy) {
  const { cornerScale, cornerNudgeX: nx, cornerNudgeY: ny } = CARD_GEOMETRY;
  const nudge = nx === 0 && ny === 0 ? "" : ` translate(${-nx}, ${-ny})`;
  const scaleSuffix =
    cornerScale === 1
      ? ""
      : ` translate(${CORNER_SCALE_ANCHOR}, ${CORNER_SCALE_ANCHOR}) scale(${cornerScale}) translate(${-CORNER_SCALE_ANCHOR}, ${-CORNER_SCALE_ANCHOR})`;
  const corr = gx === 0 && gy === 0 ? "" : ` translate(${gx}, ${gy})`;
  if (isBottom) return `rotate(180, 125, 175)${corr}${nudge}${scaleSuffix}`;
  return `${corr}${nudge}${scaleSuffix}`.trimStart();
}

/** Suit/rank's ink-normalized corner correction — zeros (today's authored position) if unset.
 * Keyed by the rendered label, not the rank id, so German/English share one entry per rank. */
function cornerInkOffset(suit, label) {
  return (
    CARD_GEOMETRY.cornerInkOffsets[`${suit}:${label}`] ?? {
      gx: 0,
      gy: 0,
      pdx: 0,
      pipDy: 0,
    }
  );
}

/** Adds rank letter/digit and a small suit pip to top-left and bottom-right corners. */
function addCornerLabels(svg, rank, suit, color, pipColor) {
  const label = rankDisplay(rank);
  const fontSize = label.length > 1 ? 56 : 64;
  const { gx, gy, pdx, pipDy } = cornerInkOffset(suit, label);
  const { cornerPipScale, cornerPipSuitFactor } = CARD_GEOMETRY;
  const pipScale = 1.8 * cornerPipScale * cornerPipSuitFactor[suit]; // 1.8 = the heroic corner-pip base size

  // Top-left and Bottom-right (rotated 180°)
  [0, 1].forEach((isBottom) => {
    const transform = cornerLabelTransform(isBottom, gx, gy);
    const g = s("g", transform ? { transform } : {});
    svg.appendChild(g); // attach before adding children so they can resolve ownerSVGElement
    // rotation-only: ty=0 + scale=1 in local space, flipped inverts y for rotate(180)
    const cornerColor = isBottom
      ? adjustGradient(svg, color, 0, 1, true)
      : color;
    const txt = s("text", {
      x: 38,
      y: 62,
      "font-family": "'Satoshi', system-ui, sans-serif",
      "font-weight": 700,
      "font-size": fontSize,
      fill: cornerColor,
      "text-anchor": "middle",
    });
    txt.textContent = label;
    g.appendChild(txt);
    addArtisanPip(g, suit, 38 + pdx, 95 + pipDy, pipScale, pipColor, isBottom);
    g.lastChild.classList.add("corner-pip");
  });
}

const sharedGameTypePipGradientIds = new Map(); // suit -> "gtp-<suit>"

const GAME_TYPE_GRADIENTS = {
  diamonds: {
    y1: -14,
    y2: 13,
    stops: [
      { offset: "0%", color: TRUMP_COLOR },
      { offset: "45%", color: TRUMP_COLOR },
      { offset: "90%", color: SUIT_COLORS.diamonds },
    ],
  },
  hearts: {
    y1: -12,
    y2: 14,
    stops: [
      { offset: "0%", color: TRUMP_COLOR },
      { offset: "33%", color: TRUMP_COLOR },
      { offset: "82%", color: SUIT_COLORS.hearts },
    ],
  },
  spades: {
    y1: -17,
    y2: 17,
    stops: [
      { offset: "0%", color: TRUMP_COLOR },
      { offset: "40%", color: TRUMP_COLOR },
      // Not one of the shared five hex registrations — a genuine blended intermediate
      // tone specific to this gradient (also hand-typed in card-gradients.js's spades stop).
      { offset: "60%", color: "#2D5A4A" },
      { offset: "90%", color: SUIT_COLORS.spades },
    ],
  },
  clubs: {
    y1: -14,
    y2: 16,
    stops: [
      { offset: "0%", color: TRUMP_COLOR },
      { offset: "47%", color: TRUMP_COLOR },
      { offset: "90%", color: SUIT_COLORS.clubs },
    ],
  },
};

// --- Main exports ---

/**
 * Creates a standalone inline SVG element containing a suit pip.
 * Used for suit indicators in UI chrome (buttons, position strips) to match card pips.
 */
export function createSuitPipSvg(suit, size, color) {
  const attrs = { viewBox: "-16 -18 32 36" };
  if (size != null) {
    attrs.width = size;
    attrs.height = size;
  }
  const svg = s("svg", attrs);
  svg.style.verticalAlign = "middle";
  addArtisanPip(svg, suit, 0, 0, 1, color);
  return svg;
}

/** Creates (once per suit) the shared game-type pip gradient in the document-level defs. */
function getSharedGameTypePipGradient(suit) {
  const cached = sharedGameTypePipGradientIds.get(suit);
  if (cached != null) return cached;

  const grad = GAME_TYPE_GRADIENTS[suit];
  const id = `gtp-${suit}`;
  const lg = s("linearGradient", {
    id,
    gradientUnits: "userSpaceOnUse",
    x1: 0,
    y1: grad.y1,
    x2: 0,
    y2: grad.y2,
  });
  for (const { offset, color } of grad.stops) {
    lg.appendChild(s("stop", { offset, "stop-color": color }));
  }
  getSharedDefs().appendChild(lg);
  sharedGameTypePipGradientIds.set(suit, id);
  return id;
}

/**
 * Creates a suit pip with a purple-to-suit-color gradient for game type indicators.
 * The suit's natural color at the base rises upward into Royal Purple — the story
 * of a suit ascending into its trump role.
 */
export function createGameTypePip(suit, size) {
  // size omitted → let CSS own the rendered size (see createSuitPipSvg).
  const attrs = { viewBox: "-16 -18 32 36" };
  if (size != null) {
    attrs.width = size;
    attrs.height = size;
  }
  const svg = s("svg", attrs);
  svg.style.verticalAlign = "middle";
  const id = getSharedGameTypePipGradient(suit);
  addArtisanPip(svg, suit, 0, 0, 1, `url(#${id})`);
  return svg;
}

/**
 * Creates a playing card DOM element with inline SVG.
 * @param {string} suit - 'diamonds', 'hearts', 'spades', or 'clubs'
 * @param {string} rank - 'seven' through 'ace'
 * @param {object} options - { faceUp, isLegal, isTrump, backColor }
 * @returns {HTMLDivElement}
 */
export function createCard(suit, rank, options = {}) {
  // Cache consult: the deck is deterministic (32 non-trump faces), so a warmed
  // template clone is ~2.9× cheaper than the construction below. See card-template-cache.js.
  const cloned = cloneFaceTemplate(suit, rank, options);
  if (cloned) return cloned;

  const { faceUp = true } = options;
  const div = document.createElement("div");
  div.className = "card";
  div.dataset.suit = suit;
  div.dataset.rank = rank;
  if (options.code != null) div.dataset.code = options.code;

  if (!faceUp) {
    div.classList.add("face-down");
    div.appendChild(createBackSvg(options.backColor));
    return div;
  }

  const flags = resolveTrumpFlags(suit, rank, options);
  decorateFaceUpDiv(div, options, flags);
  const { svg, color, pipColor } = buildCardCanvas(suit, flags);

  addCornerLabels(svg, rank, suit, color, pipColor);
  addPointValueTally(svg, rank, color);
  paintRankArt(svg, rank, suit, color);
  if (flags.trumpReveal && !flags.useGradient) markJackTrumpElements(svg, suit);

  div.appendChild(svg);
  // Inverted card style — the Null-game visual identity, plus a dev flag that forces it
  // everywhere for play-testing (card-style.js#effectiveCardStyle / card-invert.js). The
  // classic canvas is built first, then swapped vellum<->ink as a post-pass. `options.cardStyle`
  // lets a caller pin the style (card-lab drives its own colour modes and passes "classic"; the
  // invert-wash's own target build and the Null boundary retrofit both pin "inverted").
  if ((options.cardStyle ?? effectiveCardStyle()) === "inverted")
    invertCardColors(div, options);
  return div;
}

/** Resolves the trump-state flags that drive colouring, class decoration, and reveal pre-marking. */
function resolveTrumpFlags(suit, rank, options) {
  const isTrump = !!options.isTrump;
  const trumpReveal = isTrump && !!options.trumpReveal;
  const isJack = rank === "jack";
  const useGradient = isTrump && !isJack && CARD_TRUMP_GRADIENTS[suit] != null;
  return { isTrump, trumpReveal, isJack, useGradient };
}

/** Applies face-up div-level decorations: legality hint + trump-reveal attributes. */
function decorateFaceUpDiv(div, options, flags) {
  if (options.isLegal) div.classList.add("legal");
  if (flags.trumpReveal) {
    div.classList.add("trump-pending");
    div.dataset.trumpColor = TRUMP_COLOR;
    if (flags.useGradient) div.dataset.trumpGradient = "true";
  }
}

/** Creates the SVG canvas with vellum background + colour stripe; returns the stroke colours. */
function buildCardCanvas(suit, flags) {
  const svg = s("svg", { viewBox: "0 0 250 350", xmlns: SVG_NS });
  const color = resolveCardColor(svg, suit, flags);
  const pipColor = flags.isTrump && flags.isJack ? SUIT_COLORS[suit] : color;
  const { edgeThickness } = CARD_GEOMETRY;
  svg.appendChild(
    s("rect", { x: 0, y: 0, width: 250, height: 350, fill: "#E2E8F0" }),
  );
  svg.appendChild(
    s("rect", { x: 0, y: 0, width: edgeThickness, height: 350, fill: color }),
  );
  svg.appendChild(
    s("rect", {
      x: 0,
      y: 350 - edgeThickness,
      width: 250,
      height: edgeThickness,
      fill: color,
    }),
  );
  return { svg, color, pipColor };
}

/** Resolves the primary stroke colour: trump gradient URL, purple, or suit hue. */
function resolveCardColor(svg, suit, flags) {
  if (flags.useGradient)
    return addCardTrumpGradient(
      svg,
      suit,
      SUIT_COLORS[suit],
      flags.trumpReveal,
    );
  if (flags.isTrump && !flags.trumpReveal) return TRUMP_COLOR;
  return SUIT_COLORS[suit];
}

/** Paints the rank-specific centre art (ace pip, face motif, or pip arrangement). */
function paintRankArt(svg, rank, suit, color) {
  if (rank === "ace") {
    addAceMotif(svg, suit, color);
  } else if (rank === "jack" || rank === "queen" || rank === "king") {
    addFaceMotif(svg, rank, color);
  } else {
    addPipArrangement(svg, suit, rank, color);
  }
}

/** Jack trump-reveal path: pre-mark elements whose fill/stroke will animate to TRUMP_COLOR. */
function markJackTrumpElements(svg, suit) {
  const suitColor = SUIT_COLORS[suit];
  svg.querySelectorAll("[fill], [stroke]").forEach((el) => {
    if (el.closest(".corner-pip")) return;
    const fill = el.getAttribute("fill");
    const stroke = el.getAttribute("stroke");
    if (fill && fill !== "none" && fill !== "#E2E8F0")
      el.classList.add("trump-recolor-fill");
    if (stroke && stroke !== "none" && stroke === suitColor)
      el.classList.add("trump-recolor-stroke");
  });
}

/**
 * Trump-retrofit seam: rebuild an existing face-up card's
 * inner SVG canvas for new trump flags while keeping the OUTER `.card` node's identity —
 * dataset, position vars, interaction classes, and (delegated) listeners all survive.
 * Needed because trump visuals are baked into the canvas at creation (resolveCardColor /
 * markJackTrumpElements); a class toggle cannot retrofit them onto a card created before
 * the game type existed. Builds a throwaway reference card with the requested options and
 * adopts its canvas + trump decorations, so the result is `createCard`'s by construction:
 * with `{ isTrump: true, trumpReveal: true }` the card becomes trump-PENDING (suit color
 * showing, reveal pre-marked for revealTrumpColor); with `{ isTrump: true }` it becomes the
 * static trump canvas a fresh render builds — the boundary mutation's settle-time
 * canonicalization (resting DOM equals the from-scratch projection, not resembles it).
 */
export function retrofitCardCanvas(cardEl, options = {}) {
  const fresh = createCard(cardEl.dataset.suit, cardEl.dataset.rank, options);
  cardEl
    .querySelector(":scope > svg")
    .replaceWith(fresh.querySelector(":scope > svg"));
  cardEl.classList.toggle(
    "trump-pending",
    fresh.classList.contains("trump-pending"),
  );
  cardEl.classList.remove("trump-revealed"); // never on a fresh card
  if (fresh.dataset.trumpColor) {
    cardEl.dataset.trumpColor = fresh.dataset.trumpColor;
  } else {
    delete cardEl.dataset.trumpColor;
  }
  if (fresh.dataset.trumpGradient) {
    cardEl.dataset.trumpGradient = "true";
  } else {
    delete cardEl.dataset.trumpGradient;
  }
}

/**
 * The reveal-onto-anonymous-node sibling of `retrofitCardCanvas`'s "resting DOM equals the
 * from-scratch projection" contract above — that helper assumes the node already carries
 * suit/rank; this one stamps the identity an anonymous (face-down) node needs so it matches
 * `createCard`'s from-scratch build once the face SVG is swapped in.
 */
export function markCardRevealed(el, card) {
  el.dataset.suit = card.suit;
  el.dataset.rank = card.rank;
  if (card.code != null) el.dataset.code = card.code;
  el.classList.remove("face-down");
}

/**
 * Transitions a trump-pending card from suit color to trump appearance.
 * Non-Jack cards: animates gradient stop-colors (purple washes down from top).
 * Jacks: animates fill/stroke to solid TRUMP_COLOR (existing behavior).
 */
export function revealTrumpColor(cardEl) {
  const trumpColor = cardEl.dataset.trumpColor;
  if (!trumpColor) return;
  const svgEl = cardEl.querySelector("svg");
  if (!svgEl) return;

  if (cardEl.dataset.trumpGradient) {
    revealTrumpGradient(svgEl);
  } else {
    // Jack path: solid-color fill/stroke transition
    svgEl.querySelectorAll(".trump-recolor-fill").forEach((el) => {
      el.style.transition = `fill ${ANIMATION.TRUMP_REVEAL}ms ease-out`;
      el.style.fill = trumpColor;
    });
    svgEl.querySelectorAll(".trump-recolor-stroke").forEach((el) => {
      el.style.transition = `stroke ${ANIMATION.TRUMP_REVEAL}ms ease-out`;
      el.style.stroke = trumpColor;
    });
  }

  cardEl.classList.remove("trump-pending");
  cardEl.classList.add("trump-revealed");
}
